import { List, useStyles, Caption, useTheme } from '@archipro-design/aria';
import * as S from './DetailsTab.style';
import type Product from '../../type/product';

interface DetailsTabProps {
    data: Product;
}

const DetailsTab = ({ data }: DetailsTabProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const categories = data.Categories.map<string>((category) => {
        return category.PrettyTitle;
    });

    const productInfoList = [
        { label: 'Category', value: categories.join(', ') },
        { label: 'Range', value: data.LibraryRange?.Title },
        { label: 'Brand', value: data.Brand },
        { label: 'Designer', value: data.Designer },
        { label: 'Year', value: data.Year },
        { label: 'Warranty', value: data.WarrantyDuration },
    ];

    const filteredProductInfoList = productInfoList
        .filter((option) => !!option.value)
        .map(({ label, value }) => {
            return {
                label: {
                    children: label,
                    styles: {
                        fontSize: 'unset',
                    },
                    className: `${css(
                        S.ProductDetailsListItemLabel
                    )} product-details-list-item-label`,
                },
                customChildComponent: (
                    <div
                        className={`${css(
                            S.ProductDetailsListItemValue
                        )} product-details-list-item-value`}
                    >
                        {value}
                    </div>
                ),
            };
        });

    const facetOptions = data.FacetOptions.filter((option) => option.Approved)
        .reduce<
            {
                ID: number;
                Label: string;
                IsGeneral: boolean;
                options: string[];
            }[]
        >((acc, curr) => {
            const {
                Facet: { ID, Label, IsGeneral },
                Name,
            } = curr;
            const facet = acc.find((facet) => facet.ID === ID);
            if (facet) {
                facet.options.push(Name);
            } else {
                acc.push({
                    ID: ID,
                    Label,
                    IsGeneral,
                    options: [Name],
                });
            }
            return acc;
        }, [])
        .sort((a, b) => a.ID - b.ID);

    const categoryFacetAttrsList = facetOptions
        .filter(({ IsGeneral }) => !IsGeneral)
        .map(({ Label: label, options }) => ({
            label: {
                children: label,
                className: `${css(
                    S.ProductDetailsListItemLabel
                )} product-details-list-item-label`,
            },
            customChildComponent:
                options.length === 1 ? (
                    <div
                        className={`${css(
                            S.ProductDetailsListItemValue
                        )} product-details-list-item-value`}
                    >
                        {options[0]}
                    </div>
                ) : (
                    <ul className={css(S.AttributeOptions)}>
                        {options.map((option) => (
                            <li
                                className={`${css(
                                    S.ProductDetailsListItemValue
                                )} product-details-list-item-value`}
                                key={option}
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                ),
        }));

    const generalFacetAttrsList = facetOptions
        .filter(({ IsGeneral }) => IsGeneral)
        .map(({ Label: label, options }) => ({
            label: {
                children: label,
                className: `${css(
                    S.ProductDetailsListItemLabel
                )} product-details-list-item-label`,
            },
            customChildComponent:
                options.length === 1 ? (
                    <div
                        className={`${css(
                            S.ProductDetailsListItemValue
                        )} product-details-list-item-value`}
                    >
                        {options[0]}
                    </div>
                ) : (
                    <ul className={css(S.AttributeOptions)}>
                        {options.map((option) => (
                            <li
                                className={`${css(
                                    S.ProductDetailsListItemValue
                                )} product-details-list-item-value`}
                                key={option}
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                ),
        }));

    return (
        <div>
            {filteredProductInfoList.length > 0 && (
                <div className={css(S.ProductDetailsContainer)}>
                    <List
                        items={filteredProductInfoList}
                        className={css(S.ProductDetailsList)}
                        variables={{
                            listBorderColor:
                                theme.siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}

            {categoryFacetAttrsList.length > 0 && (
                <div className={css(S.AttributeContainer)}>
                    <Caption className={css(S.AttributeCaption)} variant="03">
                        Attributes
                    </Caption>
                    <List
                        items={categoryFacetAttrsList}
                        className={css(S.ProductDetailsList)}
                        variables={{
                            listBorderColor:
                                theme.siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}
            {generalFacetAttrsList.length > 0 && (
                <div className={css(S.AttributeContainer)}>
                    <Caption className={css(S.AttributeCaption)} variant="03">
                        General
                    </Caption>
                    <List
                        items={generalFacetAttrsList}
                        className={css(S.ProductDetailsList)}
                        variables={{
                            listBorderColor:
                                theme.siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default DetailsTab;
